// @owners { team: patients-team }
import {
  ActionSheetContext,
  Body,
  Button,
  ButtonGroup,
  Column,
  Description,
  H2,
  InlineAlert,
  InputCheckbox,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  Tag,
  Toast,
  ToastContext,
} from '@alto/design-system';
// eslint-disable-next-line @alto/prefer-react-hook-form
import { Form, Formik, type FormikProps } from 'formik';
import React, { useContext } from 'react';
import { completeOnboarding } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers, updateUser } from '~shared/actions/users';
import { getCompanyName } from '~shared/config/getCompanyName';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import getUserPhiAuth from '~shared/features/users/selectors/getUserPhiAuth';
import {
  type CommunicationPreferencesFormFields,
  PREFERRED_COMMUNICATION_OPTIONS,
  SECONDARY_COMMUNICATION_PREFERENCE_OPTIONS,
  SECURE_MESSAGE,
  validate,
} from '~shared/helpers/communicationPreferences';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getUserError from '~shared/selectors/ui/errors/getUserError';
import getUpdateUserLoading from '~shared/selectors/ui/loading/getUpdateUserLoading';
import { useDispatchShared, useSelectorShared } from '~shared/store';

export const showNotificationType = (preferredCommunication: string | undefined) =>
  preferredCommunication === SECURE_MESSAGE;

type Props = {
  isOnboarding?: boolean;
};

export const CommunicationPreferencesForm = ({ isOnboarding = false }: Props) => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { goToPreviousActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const userError = useSelectorShared(getUserError);
  const updateUserLoading = useSelectorShared(getUpdateUserLoading);
  const user = useSelectorShared(getCurrentUser);
  const user_phi_data = useSelectorShared(getUserPhiAuth);
  const { isFeatureDisabled: shouldDisablePushNotification, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('push_notification');

  let initialPushAuthorized: boolean;
  if (shouldDisablePushNotification) {
    initialPushAuthorized = false;
  } else if (isOnboarding) {
    initialPushAuthorized = true;
  } else {
    initialPushAuthorized = user_phi_data?.push_authorized ?? false;
  }

  const initialValues: CommunicationPreferencesFormFields = {
    preferred_communication: isOnboarding ? SECURE_MESSAGE : user?.preferred_communication || SECURE_MESSAGE,
    secondary_communication_preference: user?.secondary_communication_preference,
    phi_auth_data: {
      sms_authorized: isOnboarding ? true : (user_phi_data?.sms_authorized ?? false),
      email_authorized: isOnboarding ? true : (user_phi_data?.email_authorized ?? false),
      push_authorized: initialPushAuthorized,
    },
  };

  const goBack = () => {
    if (isOnboarding) {
      goToPreviousActionSheet();
    } else {
      closeActionSheet();
    }
  };

  const onSubmit = async (data: CommunicationPreferencesFormFields) => {
    const { preferred_communication, secondary_communication_preference, phi_auth_data } = data;
    // filter out hidden fields
    const user: Partial<CommunicationPreferencesFormFields> = {
      preferred_communication,
    };

    if (showNotificationType(preferred_communication)) {
      user.secondary_communication_preference = secondary_communication_preference;
    }

    try {
      // eslint-disable-next-line import/no-deprecated
      const { success } = await dispatch(updateUser({ ...user, phi_auth_data: JSON.stringify(phi_auth_data) }));
      if (!success) return;

      if (isOnboarding) {
        // eslint-disable-next-line import/no-deprecated
        dispatch(fetchUsers());
        dispatch(completeOnboarding());
        trackEvent({ event: EVENTS.ONBOARDING__COMPLETED });
      }
      addToast(<Toast>Successfully updated communication preferences</Toast>);
      closeActionSheet();
    } catch {
      addToast(<Toast variant="error">{`${DEFAULT_ERROR_ALERT_TITLE}. ${DEFAULT_ERROR_ALERT_MESSAGE}`}</Toast>);
    }
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validate}
    >
      {({
        values,
        errors,
        handleChange,
        handleSubmit,
        setFieldValue,
      }: FormikProps<CommunicationPreferencesFormFields>) => {
        return (
          <Form noValidate>
            <LgPadding>
              <Column gap="LG">
                {isOnboarding ? (
                  <InlineAlert>
                    <Description>
                      We’ll send you medication and delivery updates with secure in-app messages.
                    </Description>
                  </InlineAlert>
                ) : (
                  <Body>How would you like {getCompanyName()} to contact you about your prescriptions?</Body>
                )}
                {userError ? (
                  <InlineAlert type="warning">
                    <Body>{userError.message}</Body>
                  </InlineAlert>
                ) : null}
                {!isOnboarding ? (
                  <Column gap="SM">
                    <H2>Preferred communication</H2>
                    {errors.preferred_communication ? (
                      <InlineAlert type="error">
                        <Description>{errors.preferred_communication}</Description>
                      </InlineAlert>
                    ) : undefined}
                    <InputRadioGroup
                      onValueChange={handleChange('preferred_communication')}
                      defaultValue={values.preferred_communication || undefined}
                    >
                      {PREFERRED_COMMUNICATION_OPTIONS.map((option, i) => (
                        <InputRadio
                          label={option.label}
                          value={option.value}
                          key={`preferred_communication-${option.value}`}
                          tag={
                            i === 0 ? (
                              <Tag
                                label="recommended"
                                type="success"
                              />
                            ) : undefined
                          }
                        />
                      ))}
                    </InputRadioGroup>
                  </Column>
                ) : null}
                {showNotificationType(values.preferred_communication) ? (
                  <Column gap="SM">
                    <H2>Notification type</H2>
                    {errors.secondary_communication_preference ? (
                      <>
                        <InlineAlert type="error">
                          <Description>{errors.secondary_communication_preference}</Description>
                        </InlineAlert>
                      </>
                    ) : undefined}
                    <Description>
                      {shouldDisablePushNotification || isFeatureDisablingLoading
                        ? "We'll also notify you with a text or email."
                        : "We'll also notify you with a push notification and either text or email."}
                    </Description>
                    <InputRadioGroup
                      onValueChange={handleChange('secondary_communication_preference')}
                      defaultValue={values.secondary_communication_preference || undefined}
                    >
                      {SECONDARY_COMMUNICATION_PREFERENCE_OPTIONS.map((option, i) => (
                        <InputRadio
                          label={option.label}
                          value={option.value}
                          key={`secondary-preferred_communication-${option.value}`}
                          tag={
                            i === 0 ? (
                              <Tag
                                label="recommended"
                                type="success"
                              />
                            ) : undefined
                          }
                        />
                      ))}
                    </InputRadioGroup>
                  </Column>
                ) : null}
                <Column gap="SM">
                  <H2>Can we include personal health info in messages we send you?</H2>
                  <Description>
                    This allows our team to provide more detail when we communicate with you about your medication or
                    orders.
                  </Description>
                  <Column>
                    <InputCheckbox
                      name="sms_authorized"
                      label="Include in text messages/SMS"
                      onPress={() => {
                        setFieldValue('phi_auth_data.sms_authorized', !values.phi_auth_data.sms_authorized);
                      }}
                      value={values.phi_auth_data.sms_authorized}
                    />
                    <InputCheckbox
                      name="email_authorized"
                      label="Include in emails"
                      onPress={() => {
                        setFieldValue('phi_auth_data.email_authorized', !values.phi_auth_data.email_authorized);
                      }}
                      value={values.phi_auth_data.email_authorized}
                    />
                    {shouldDisablePushNotification || isFeatureDisablingLoading ? null : (
                      <InputCheckbox
                        name="push_authorized"
                        label="Include in push notifications"
                        onPress={() => {
                          setFieldValue('phi_auth_data.push_authorized', !values.phi_auth_data.push_authorized);
                        }}
                        value={values.phi_auth_data.push_authorized}
                      />
                    )}
                  </Column>
                </Column>
                <ButtonGroup
                  justifyButtons="right"
                  inline
                  buttons={[
                    <Button
                      key="dismiss-communication-preferences"
                      label={isOnboarding ? 'Back' : 'Cancel'}
                      onPress={goBack}
                      type="tertiary"
                    />,
                    <Button
                      key="submit-communication-preferences"
                      loading={updateUserLoading}
                      loadingLabel="Submitting"
                      label={isOnboarding ? 'Complete profile' : 'Update'}
                      onPress={handleSubmit}
                    />,
                  ]}
                />
              </Column>
            </LgPadding>
          </Form>
        );
      }}
    </Formik>
  );
};
